import { Product } from '@commerce/types/product'
import { titleize } from '@utils/capitalize-strings'
import Head from 'next/head'
import Link from 'next/link'
import { FC } from 'react'
import cn from 'clsx'

interface Props {
  className?: string
  product: Product
}

const ProductBreadcrumbs: FC<Props> = ({ className, product }) => {
  const addBreadcrumbsJsonLd = (
    crumbs: { position: number; label: string; path: string }[]
  ) => {
    let itemListElement = [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'Home',
        item: `${process.env.baseUrl}`,
      },
    ]

    for (const c of crumbs) {
      itemListElement.push({
        '@type': 'ListItem',
        position: c.position + 2,
        name: titleize(c.label),
        item: `${process.env.baseUrl}${c.path}`,
      })
    }

    const breadcrumbsData = {
      '@context': 'https://schema.org/',
      '@type': 'BreadcrumbList',
      itemListElement,
    }

    return {
      __html: JSON.stringify(breadcrumbsData),
    }
  }

  const permalink = product.taxons.filter((taxon) => taxon.isLeaf)[0].permalink

  const permalinkParts = permalink.split('/')

  const crumbs = permalinkParts.map((part, index) => {
    let generatedPath = `/t/${permalinkParts.slice(0, index + 1).join('/')}`

    if (generatedPath === '/t/jewelry/rings-and-bands/engagement/create-your-ring') {
      generatedPath = '/t/create-your-ring'
    } else if (generatedPath === '/t/jewelry/rings-and-bands/engagement/create-your-ring/solitaire-rings') {
      generatedPath = '/t/create-your-ring/solitaire-rings'
    } else if (generatedPath === '/t/jewelry/rings-and-bands/engagement/create-your-ring/halo-rings') {
      generatedPath = '/t/create-your-ring/halo-rings'
    } else if (generatedPath === '/t/jewelry/rings-and-bands/engagement/create-your-ring/hidden-halo-rings') {
      generatedPath = '/t/create-your-ring/hidden-halo-rings'
    } else if (generatedPath === '/t/jewelry/rings-and-bands/engagement/create-your-ring/pave-rings') {
      generatedPath = '/t/create-your-ring/pave-rings'
    } else if (generatedPath === '/t/jewelry/rings-and-bands/engagement/create-your-ring/sidestone-rings') {
      generatedPath = '/t/create-your-ring/sidestone-rings'
    } else if (generatedPath === '/t/jewelry/rings-and-bands/engagement/create-your-ring/three-stone-rings') {
      generatedPath = '/t/create-your-ring/three-stone-rings'
    } else if (generatedPath === '/t/jewelry/rings-and-bands/wedding-bands') {
      generatedPath = '/t/wedding-bands'
    } else if (generatedPath === '/t/jewelry/rings-and-bands/wedding-bands/womens-classic-bands') {
      generatedPath = '/t/wedding-bands/womens-classic-bands'
    } else if (generatedPath === '/t/jewelry/rings-and-bands/wedding-bands/womens-diamond-bands') {
      generatedPath = '/t/wedding-bands/womens-diamond-bands'
    } else if (generatedPath === '/t/jewelry/rings-and-bands/wedding-bands/womens-eternity-bands') {
      generatedPath = '/t/wedding-bands/womens-eternity-bands'
    } else if (generatedPath === '/t/jewelry/rings-and-bands/wedding-bands/mens-classic-bands') {
      generatedPath = '/t/wedding-bands/mens-classic-bands'
    } else if (generatedPath === '/t/jewelry/rings-and-bands/wedding-bands/mens-diamond-bands') {
      generatedPath = '/t/wedding-bands/mens-diamond-bands'
    } else if (generatedPath === '/t/jewelry/rings-and-bands') {
      generatedPath = '/t/fine-jewelry/rings-and-bands'
    } else if (generatedPath === '/t/jewelry/rings-and-bands/engagement') {
      generatedPath = '/t/create-your-ring'
    } else if (generatedPath === '/t/jewelry') {
      generatedPath = '/t/fine-jewelry'
    }

    return {
      position: index,
      label: part.replaceAll('-', ' ').replaceAll(' and ', ' & '),
      path: generatedPath, 
    }
  })

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={addBreadcrumbsJsonLd(crumbs)}
          key="product-breadcrumbs-jsonld"
        />
      </Head>
      <nav
        className={`yd-breadcrumbs-container ${className}`}
        aria-label="breadcrumbs"
      >
        <ol className="yd-breadcrumbs-list">
          <Link href="/">
            <a>
              <li className="yd-breadcrumbs-inactive-item">Home</li>
            </a>
          </Link>
          {crumbs &&
            crumbs.map((crumb, index) => {
              const isLastItem = index === crumbs.length - 1
              return (
                <Link href={crumb.path} key={crumb.position}>
                  <a>
                    <li
                      className={cn(
                        { 'yd-breadcrumbs-inactive-item': !isLastItem },
                        { 'yd-breadcrumbs-active-item': isLastItem }
                      )}
                    >
                      {crumb.label}
                    </li>
                  </a>
                </Link>
              )
            })}
        </ol>
      </nav>
    </>
  )
}

export default ProductBreadcrumbs
